// auth.service.ts
import { Injectable } from '@angular/core';
import { OAuthEvent, OAuthService } from 'angular-oauth2-oidc';
import { authConfig } from './auth.config';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly redirectUrlStorageKey = 'auth_redirect_url';

  private isRefreshing = false;
  public userStore = new BehaviorSubject<any>(null);

  // This method is used to initialize the class instance using oauth service and router
  constructor(
    private oauthService: OAuthService,
    private router: Router,
  ) {
    this.configureSSO();
  }

  // This method is used to configure sso
  private configureSSO() {
    this.oauthService.configure(authConfig);
    this.oauthService.setStorage(localStorage);

    this.oauthService.loadDiscoveryDocumentAndTryLogin().then(() => {
      if (this.oauthService.hasValidAccessToken()) {
        this.setupTokenEvents();
      }
    });
  }

  // This method is used to set up token events
  private setupTokenEvents() {
    this.oauthService.events.subscribe((event: OAuthEvent) => {
      switch (event.type) {
        case 'token_expires':
          this.manualRefreshToken();
          break;

        case 'silent_refresh_error':
          this.handleRefreshFailure();
          break;

        case 'token_received':
          this.updateUser();
          break;
      }
    });
  }

  // This method is used to manual refresh token
  private manualRefreshToken() {
    if (this.isRefreshing) return;

    this.isRefreshing = true;
    this.oauthService.refreshToken().then(() => {
      //console.log('✅ Token refresh successful');
      const claims = this.oauthService.getIdentityClaims();
      const idToken = this.oauthService.getIdToken();
      const accessToken = this.oauthService.getAccessToken();
      const userObj = { user: claims, token: idToken, access_token: accessToken };
      this.updateUserStore(userObj)
      this.userStore.next(userObj);
    }).catch(err => {
      this.handleRefreshFailure();
    }).finally(() => {
      this.isRefreshing = false;
    });
  }

  // This method is used to update user
  private updateUser() {
    //console.log('✅ Token received here');
    const claims = this.oauthService.getIdentityClaims();
    const idToken = this.oauthService.getIdToken();
    const accessToken = this.oauthService.getAccessToken();
    const userObj = { user: claims, token: idToken, access_token: accessToken };
    this.updateUserStore(userObj)
    this.userStore.next(userObj);
  }

  // This method is used to handle refresh failure
  private handleRefreshFailure() {
    console.warn('❌ Refresh token failed or expired.');
    this.logout();
  }

  // This method is used to log in
  login() {
    this.oauthService.initCodeFlow();
  }

  // This method is used to log out
  logout() {
    this.oauthService.logOut();
    // Save selected language
    let lang = localStorage.getItem("selectedLang");
    // Clear storages
    sessionStorage.clear();
    localStorage.clear();
    // Restore language
    if (lang) {
      localStorage.setItem("selectedLang", lang);
    }
  }

  // This method is used to check if logged in
  isLoggedIn(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  // This method is used to get access token
  getAccessToken(): string | null {
    return this.oauthService.getAccessToken();
  }

  // This method is used to get refresh token
  getRefreshToken(): string | null {
    return this.oauthService.getRefreshToken();
  }

  // This method is used to get id token
  getIdToken(): string | null {
    return this.oauthService.getIdToken();
  }

  // This method is used to get user claims
  getUserClaims(): any {
    return this.oauthService.getIdentityClaims();
  }

  // This method is used to load user profile
  loadUserProfile(): Promise<any> {
    return this.oauthService.loadUserProfile();
  }

  // This method is used to refresh token
  refreshToken() {
    this.oauthService.refreshToken().then((data) => {
      const idToken: any = this.oauthService.getIdToken();
      const claims: any = this.oauthService.getIdentityClaims();
      const access_token: any = this.oauthService.getAccessToken();
      let userObj = { user: claims, token: idToken, access_token: access_token };
      // this.store.dispatch(loginSuccess(userObj));
      this.updateUserStore(userObj)
    })
  }

  setRedirectUrl(url: string) {
    localStorage.setItem(this.redirectUrlStorageKey, url);
  }

  getRedirectUrl(): string | null {
    return localStorage.getItem(this.redirectUrlStorageKey);
  }

  consumeRedirectUrl(): string | null {
    const redirectUrl = this.getRedirectUrl();
    if (redirectUrl) {
      localStorage.removeItem(this.redirectUrlStorageKey);
    }
    return redirectUrl;
  }

  // This method is used to update user store using data
  updateUserStore(data: any) {
    localStorage.setItem('login_user', JSON.stringify(data));
  }
}
